/**
 * ShelfLife offline storage layer.
 *
 * Wraps IndexedDB via `idb-keyval` and exposes three logical stores:
 *
 *   1. `catalog`   — the user's scoped product catalog (department/store).
 *                    Used as the first-hop lookup source for scan + search
 *                    so weak-Wi-Fi areas (back-of-house freezers, etc.)
 *                    don't break the workflow.
 *   2. `inventory` — the most recent /api/inventory snapshot for the
 *                    current user. Read-only mirror; the source of truth
 *                    is the server.
 *   3. `outbox`    — queued mutations (POST /api/inventory and
 *                    POST /api/inventory/{id}/remove only). Each entry
 *                    survives reloads and is drained by `syncRunner`.
 *
 * Storage rules:
 *   - JWTs and passwords NEVER enter IndexedDB. Tokens stay in
 *     `localStorage` (existing behavior).
 *   - All keys are namespaced by `userId` so a shared device cannot
 *     leak data between accounts. `clearUserData(userId)` wipes only
 *     the leaving user's keys.
 *   - Anything not explicitly enumerated here is OUT of scope for the
 *     offline MVP (no admin caching, no notification mirror, no
 *     pagination prefetch).
 */
import { get, set, del, keys, createStore } from "idb-keyval";

// One physical IDB database/store; logical separation is by key prefix.
// idb-keyval's default store is named "keyval-store" — keeping a dedicated
// store name lets us tear down all ShelfLife state in one shot.
const STORE = createStore("shelflife-offline", "kv");

// Catalog has its own version so an isolation-boundary change can invalidate
// product snapshots without orphaning inventory or queued outbox mutations.
const VERSION = "v1";
const CATALOG_VERSION = "v2";

const NS = {
  catalog:        (userId) => `${CATALOG_VERSION}:user:${userId}:catalog`,
  legacyCatalog:  (userId) => `${VERSION}:user:${userId}:catalog`,
  inventoryList:  (userId) => `${VERSION}:user:${userId}:inventory:list`,
  outbox:         (userId, entryId) => `${VERSION}:user:${userId}:outbox:${entryId}`,
  outboxPrefix:   (userId) => `${VERSION}:user:${userId}:outbox:`,
};

/* ----------------------- Catalog (read cache) ---------------------------- */

function sanitizeCatalogProducts(products) {
  if (!Array.isArray(products)) return [];
  return products
    .filter((product) => product && typeof product === "object" && !Array.isArray(product))
    .map((product) => {
      const safeProduct = { ...product };
      delete safeProduct.product_id;
      delete safeProduct.source_item_id;
      delete safeProduct.alt_codes;
      delete safeProduct.alternate_codes;
      return safeProduct;
    });
}

export async function saveCatalog(userId, products, scope = {}) {
  if (!userId) return;
  try {
    await del(NS.legacyCatalog(userId), STORE).catch(() => {});
    await set(
      NS.catalog(userId),
      {
        fetchedAt: Date.now(),
        scope,                // { department_id, store_id } at time of fetch
        products: sanitizeCatalogProducts(products),
      },
      STORE,
    );
  } catch (e) {
    // Never let an IDB write failure (quota, transaction abort, iOS
    // Safari private-tab restriction) bubble up into the caller's
    // render path. The app must remain usable without offline cache.
    if (typeof console !== "undefined") console.warn("offlineStore.saveCatalog failed:", e);
  }
}

export async function readCatalog(userId) {
  if (!userId) return null;
  try {
    await del(NS.legacyCatalog(userId), STORE).catch(() => {});
    const v = await get(NS.catalog(userId), STORE);
    return v || null;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.readCatalog failed:", e);
    return null;
  }
}

/**
 * Find a product in the local catalog by barcode or any code-like field.
 * Mirrors the server's "code resolver" leniency at a coarse level —
 * exact match on the most likely fields. Returns null if not found.
 *
 * Defensive: every IDB / property access is guarded so a corrupt cache,
 * an iOS Safari IDB-blocked context, or a half-written entry can never
 * crash the calling page (e.g. ScanPage's `loadProduct`).
 */
export async function findLocalProductByCode(userId, code) {
  try {
    const c = (code || "").toString().trim();
    if (!userId || !c) return null;
    const wrap = await readCatalog(userId);
    if (!wrap || !Array.isArray(wrap.products)) return null;
    for (const p of wrap.products) {
      if (!p) continue;
      if (p.barcode && String(p.barcode).trim() === c) return p;
    }
    // Last-chance digits-only match for scale-style codes.
    const digits = c.replace(/\D/g, "");
    if (digits.length >= 4) {
      for (const p of wrap.products) {
        if (p && String(p.barcode || "").replace(/\D/g, "") === digits) return p;
      }
    }
    return null;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.findLocalProductByCode failed:", e);
    return null;
  }
}

export async function searchLocalProducts(userId, query, limit = 20) {
  try {
    const q = (query || "").toString().trim().toLowerCase();
    if (!userId || !q) return [];
    const wrap = await readCatalog(userId);
    if (!wrap || !Array.isArray(wrap.products)) return [];
    const out = [];
    for (const p of wrap.products) {
      if (!p || p.active === false) continue;
      const name = (p.name || "").toLowerCase();
      const bc = (p.barcode || "").toString().toLowerCase();
      if (name.includes(q) || bc.includes(q)) {
        out.push(p);
        if (out.length >= limit) break;
      }
    }
    return out;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.searchLocalProducts failed:", e);
    return [];
  }
}

/* ----------------------- Inventory snapshot ------------------------------ */

export async function saveInventory(userId, items) {
  if (!userId) return;
  try {
    await set(
      NS.inventoryList(userId),
      { fetchedAt: Date.now(), items: Array.isArray(items) ? items : [] },
      STORE,
    );
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.saveInventory failed:", e);
  }
}

export async function readInventory(userId) {
  if (!userId) return null;
  try {
    const v = await get(NS.inventoryList(userId), STORE);
    return v || null;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.readInventory failed:", e);
    return null;
  }
}

/* ----------------------- Outbox ----------------------------------------- */

/**
 * Outbox entry shape:
 *   {
 *     id:           string  // uuid
 *     userId:       string
 *     kind:         "add_inventory" | "remove_inventory"
 *     url:          string  // axios URL (no host)
 *     method:       "POST"
 *     body:         object  // request body
 *     createdAt:    number  // Date.now()
 *     attempts:     number  // increments on each failed sync
 *     lastError:    string|null
 *     status:       "pending" | "syncing" | "error" | "synced"
 *     optimisticId: string|null   // for add: synthetic inventory row id
 *     targetId:     string|null   // for remove: real inventory id
 *   }
 */

const MAX_ATTEMPTS = 5;

function uuidv4() {
  // crypto.randomUUID is available in all modern browsers + TWA WebView.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export async function enqueueOutbox(userId, entry) {
  if (!userId) throw new Error("enqueueOutbox requires userId");
  const id = uuidv4();
  const full = {
    id,
    userId,
    kind: entry.kind,
    url: entry.url,
    method: entry.method || "POST",
    body: entry.body || {},
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
    status: "pending",
    optimisticId: entry.optimisticId || null,
    targetId: entry.targetId || null,
  };
  try {
    await set(NS.outbox(userId, id), full, STORE);
  } catch (e) {
    // IDB write failure is a real risk on quota-stressed devices.
    // Surface it as a rejected promise so the caller (e.g. ScanPage)
    // can toast the user that the action was NOT queued — better than
    // a silent black hole that the operator never finds out about.
    if (typeof console !== "undefined") console.warn("offlineStore.enqueueOutbox failed:", e);
    throw e;
  }
  return full;
}

export async function listOutbox(userId) {
  if (!userId) return [];
  try {
    const allKeys = await keys(STORE);
    const prefix = NS.outboxPrefix(userId);
    const mine = allKeys.filter((k) => typeof k === "string" && k.startsWith(prefix));
    if (mine.length === 0) return [];
    const out = [];
    for (const k of mine) {
      try {
        const v = await get(k, STORE);
        if (v) out.push(v);
      } catch (_) { /* skip corrupt entry */ }
    }
    out.sort((a, b) => a.createdAt - b.createdAt);
    return out;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.listOutbox failed:", e);
    return [];
  }
}

export async function updateOutboxEntry(userId, entry) {
  if (!userId || !entry || !entry.id) return;
  try {
    await set(NS.outbox(userId, entry.id), entry, STORE);
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.updateOutboxEntry failed:", e);
  }
}

export async function removeOutboxEntry(userId, entryId) {
  if (!userId || !entryId) return;
  try {
    await del(NS.outbox(userId, entryId), STORE);
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.removeOutboxEntry failed:", e);
  }
}

export async function countPendingOutbox(userId) {
  try {
    const entries = await listOutbox(userId);
    return entries.filter((e) => e.status !== "synced").length;
  } catch (_) {
    return 0;
  }
}

export const OUTBOX_MAX_ATTEMPTS = MAX_ATTEMPTS;

/* ----------------------- User-scoped tear-down --------------------------- */

/**
 * Wipe every IDB key that belongs to ``userId``. Called on logout and
 * (when the operator confirms) on any "outbox would be discarded" flow.
 */
export async function clearUserData(userId) {
  if (!userId) return;
  try {
    const allKeys = await keys(STORE);
    const mine = allKeys.filter(
      (k) =>
        typeof k === "string" &&
        (k === NS.catalog(userId) ||
          k === NS.legacyCatalog(userId) ||
          k === NS.inventoryList(userId) ||
          k.startsWith(NS.outboxPrefix(userId))),
    );
    await Promise.all(mine.map((k) => del(k, STORE).catch(() => {})));
  } catch (e) {
    if (typeof console !== "undefined") console.warn("offlineStore.clearUserData failed:", e);
  }
}

/* ----------------------- Utilities --------------------------------------- */

export function isOnline() {
  return typeof navigator === "undefined" ? true : !!navigator.onLine;
}
